/**
 * Worker bank and Aadhaar helpers. Only the last 4 Aadhaar digits ever leave the phone: as soon as
 * the 12th digit is typed the field turns into "XXXX XXXX 1234".
 */

export const AADHAAR_MASK = "XXXX XXXX ";

/** "XXXX XXXX 1234", or "" without last 4 digits. */
export const maskAadhaar = (last4?: string | null) => (last4 && /^\d{4}$/.test(last4) ? AADHAAR_MASK + last4 : "");

/**
 * What the Aadhaar field shows after a keystroke. 12 digits (spaces allowed) become the mask at
 * once; a masked value stays masked; anything shorter is kept as typed so the person can finish.
 */
export const aadhaarInput = (raw: string): string => {
  const value = raw || "";
  if (value.toUpperCase().startsWith("XXXX")) {
    // Editing the masked value: any change clears it so the number is typed again.
    const last4 = value.replace(/\D/g, "");
    return last4.length === 4 && value.toUpperCase() === AADHAAR_MASK + last4 ? value.toUpperCase() : "";
  }
  const digits = value.replace(/\D/g, "").slice(0, 12);
  if (digits.length === 12) return maskAadhaar(digits.slice(-4));
  // Group as 4-4-4 while typing.
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
};

/** What is sent to the server: the mask, "" to clear, or null when the field is half-typed. */
export const aadhaarToSave = (shown: string): string | null => {
  const value = (shown || "").trim();
  if (!value) return "";
  return /^XXXX XXXX \d{4}$/i.test(value) ? value.toUpperCase() : null;
};

export const IFSC = /^[A-Z]{4}0[A-Z0-9]{6}$/;

export const normalizeIfsc = (raw: string) => (raw || "").replace(/\s+/g, "").toUpperCase();

/** "XXXX1234" for showing an account number in passing. */
export const maskAccount = (account?: string | null) => {
  const digits = (account || "").replace(/\D/g, "");
  return digits ? `XXXX${digits.slice(-4)}` : "";
};

export type KycPhotoKind = "aadhaar-front" | "aadhaar-back" | "passbook";

export const KYC_PHOTOS: { kind: KycPhotoKind; label: string }[] = [
  { kind: "aadhaar-front", label: "Aadhaar front" },
  { kind: "aadhaar-back", label: "Aadhaar back" },
  { kind: "passbook", label: "Passbook or cheque" },
];

/** Server file type for a picked photo; null for anything that isn't a JPEG, PNG or HEIC picture. */
export const photoFileType = (mime: string): string | null => {
  const m = (mime || "").toLowerCase();
  if (m === "image/jpeg" || m === "image/jpg") return "image/jpeg";
  if (m === "image/png") return "image/png";
  if (m === "image/heic" || m === "image/heif") return "image/heic";
  return null;
};
